import React, { useState, useEffect, useRef } from 'react';
import { messageService } from '../../services/api';
import './MessageCenter.css';

const MessageCenter = ({ user, profile }) => {
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [conversations, setConversations] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showBroadcast, setShowBroadcast] = useState(false);
    const [broadcastContent, setBroadcastContent] = useState('');
    const [broadcastLoading, setBroadcastLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const messagesEndRef = useRef(null);

    const isAdmin = profile?.role?.toUpperCase() === 'ADMIN';

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (!profile) return;
        if (isAdmin) {
            loadConversations();
        } else {
            loadAdminAndMessages();
        }
    }, [isAdmin, profile]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (isAdmin) {
                loadConversations();
                if (selectedUser) loadMessages(selectedUser.id);
            } else {
                loadAdminAndMessages(true);
            }
        }, 5000);
        return () => clearInterval(interval);
    }, [isAdmin, selectedUser]);

    const loadConversations = async () => {
        try {
            const users = await messageService.getMessagingUsers();
            setConversations(users);
        } catch (error) {
            console.error('Error loading conversations:', error);
        }
    };

    const loadAdminAndMessages = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const admin = await messageService.getAdmin();
            if (admin) {
                const history = await messageService.getConversation(admin.id);
                setMessages(history);
                setSelectedUser(admin);
            }
        } catch (error) {
            console.error('Error loading admin conversation:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const loadMessages = async (userId) => {
        try {
            const history = await messageService.getConversation(userId);
            setMessages(history);
        } catch (error) {
            console.error('Error loading messages:', error);
        }
    };

    const handleSelectUser = (user) => {
        setSelectedUser(user);
        setShowBroadcast(false);
        loadMessages(user.id);
    };

    const handleSendBroadcast = async (e) => {
        e.preventDefault();
        if (!broadcastContent.trim()) return;
        try {
            setBroadcastLoading(true);
            await messageService.sendBroadcast(broadcastContent);
            alert('Broadcast sent successfully.');
            setBroadcastContent('');
            setShowBroadcast(false);
            if (isAdmin) loadConversations();
        } catch (error) {
            console.error('Broadcast error:', error);
            alert('Failed to send broadcast.');
        } finally {
            setBroadcastLoading(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !selectedUser) return;
        try {
            const msg = await messageService.sendMessage({
                receiverId: selectedUser.id,
                content: newMessage
            });
            setMessages([...messages, msg]);
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
            alert('Failed to send message.');
        }
    };

    return (
        <div className="message-center-container">
            <div className={`messaging-card ${isAdmin ? 'admin-view' : 'user-view'}`}>
                {isAdmin && (
                    <div className="conversations-sidebar">
                        <div className="sidebar-header">
                            <h3>Messages</h3>
                            <div className="sidebar-actions">
                                <button
                                    className={`btn-broadcast-toggle ${showBroadcast ? 'active' : ''}`}
                                    onClick={() => { setShowBroadcast(true); setSelectedUser(null); }}
                                >
                                    📢 Broadcast
                                </button>
                                <div className="sidebar-search">
                                    <input
                                        type="text"
                                        placeholder="Search user..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="conversations-list">
                            {conversations
                                .filter(c => {
                                    if (!searchTerm) return true;
                                    const full = `${c.firstName} ${c.lastName} ${c.email}`.toLowerCase();
                                    return full.includes(searchTerm.toLowerCase());
                                })
                                .map(u => (
                                    <div
                                        key={u.id}
                                        className={`conversation-item ${selectedUser?.id === u.id ? 'active' : ''}`}
                                        onClick={() => handleSelectUser(u)}
                                    >
                                        <div className="user-avatar-mini">
                                            {u.firstName[0]}{u.lastName[0]}
                                        </div>
                                        <div className="user-info-mini">
                                            <div className="user-name-mini">{u.firstName} {u.lastName}</div>
                                            <div className="user-role-mini">{u.role}</div>
                                        </div>
                                        {u.unreadCount > 0 && (
                                            <div className="unread-badge-mini">{u.unreadCount}</div>
                                        )}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}

                <div className="chat-window">
                    <div className="chat-header">
                        {showBroadcast ? (
                            <div className="header-info">
                                <span className="icon">📢</span>
                                <div>
                                    <h4>Broadcast (All Users)</h4>
                                    <p>Send a notification to all active members.</p>
                                </div>
                            </div>
                        ) : selectedUser ? (
                            <>
                                <div className="user-avatar">
                                    {selectedUser.firstName[0]}{selectedUser.lastName[0]}
                                </div>
                                <div className="header-info">
                                    <h4>{selectedUser.firstName} {selectedUser.lastName}</h4>
                                    <p>{selectedUser.email}</p>
                                </div>
                            </>
                        ) : (
                            <h4>{isAdmin ? 'Select a user or action' : 'Loading...'}</h4>
                        )}
                    </div>

                    <div className="messages-display">
                        {showBroadcast ? (
                            <div className="broadcast-editor">
                                <div className="broadcast-info">
                                    <h3>📢 Send Broadcast</h3>
                                    <p>This message will be delivered to all active users in the system.</p>
                                </div>
                                <textarea
                                    className="broadcast-textarea"
                                    placeholder="Type your message here..."
                                    value={broadcastContent}
                                    onChange={(e) => setBroadcastContent(e.target.value)}
                                />
                                <div className="broadcast-actions">
                                    <button
                                        className="btn-send-broadcast"
                                        onClick={handleSendBroadcast}
                                        disabled={!broadcastContent.trim() || broadcastLoading}
                                    >
                                        {broadcastLoading ? 'Sending...' : 'Send to All ➤'}
                                    </button>
                                </div>
                            </div>
                        ) : loading ? (
                            <div className="chat-loading">Loading messages...</div>
                        ) : messages.length === 0 ? (
                            <div className="chat-empty-state">
                                <div className="icon">💬</div>
                                <p>{isAdmin ? 'Send a message to start the conversation.' : 'You can send questions to the administrator here.'}</p>
                            </div>
                        ) : (
                            messages.map((msg, idx) => (
                                <div key={idx} className={`message-bubble-wrapper ${msg.senderId === profile.id ? 'sent' : 'received'}`}>
                                    <div className="message-bubble">
                                        <div className="bubble-content">{msg.content}</div>
                                        <div className="bubble-time">
                                            {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <form className="message-input-area" onSubmit={handleSendMessage}>
                        <input
                            type="text"
                            placeholder="Type a message..."
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            disabled={!selectedUser}
                        />
                        <button type="submit" className="btn-send-message" disabled={!selectedUser || !newMessage.trim()}>
                            <span>➤</span>
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default MessageCenter;
